import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { exhaustMap, Subscription, timer } from 'rxjs';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';

import { ResponseStatus } from '@shared/models/base/response-status.model';
import { Job, JobStatus, StartDownloadRequest } from '@shared/models/download-job.model';
import { DownloadJobsService } from '@shared/services/download-jobs.service';
import { DialogComponent } from '@shared/components/organisms/dialog/dialog.component';
import { ErrorModalService } from '@shared/services/error-modal.service';
import { ErrorStore } from '@shared/stores/error.store';

const POLLING_INTERVAL_MS = 10_000;

const isActiveJob = (job: Job): boolean =>
  job.status === JobStatus.Pending || job.status === JobStatus.InProgress;

export interface DownloadJobsState {
  jobs: Job[];
  isPolling: boolean;
  initialized: boolean;
  jobsResponseStatus: ResponseStatus;
  startJobResponseStatus: ResponseStatus;
}

const initialState: DownloadJobsState = {
  jobs: [],
  isPolling: false,
  initialized: false,
  jobsResponseStatus: ResponseStatus.IDLE,
  startJobResponseStatus: ResponseStatus.IDLE,
};

export const DownloadJobsStore = signalStore(
  { providedIn: 'root', protectedState: true },
  withDevtools('download-jobs'),
  withState<DownloadJobsState>(initialState),
  withComputed((store) => ({
    pendingJobs: computed(() =>
      store.jobs().filter(isActiveJob),
    ),
  })),
  withMethods((
    store,
    downloadJobsService = inject(DownloadJobsService),
    dialog = inject(MatDialog),
    errorStore = inject(ErrorStore),
    errorModalService = inject(ErrorModalService),
  ) => {
    let pollingSubscription: Subscription | undefined;
    let requestSubscription: Subscription | undefined;
    let startDownloadSubscriptions = new Subscription();
    let downloadStartedDialogRef: MatDialogRef<DialogComponent> | undefined;

    const stopPolling = (): void => {
      pollingSubscription?.unsubscribe();
      pollingSubscription = undefined;
      updateState(store, '[Download Jobs] Polling stopped', {
        isPolling: false,
      });
    };

    const reportResponseError = (message: string | undefined, fallbackMessage: string): void => {
      errorStore.logError({
        success: false,
        code: 'DOWNLOAD_REQUEST_FAILED',
        message: message ?? fallbackMessage,
      });
      errorModalService.showErrorModal();
    };

    const reportRequestError = (error: unknown, fallbackMessage: string): void => {
      const code = error instanceof HttpErrorResponse ? error.status : 'DOWNLOAD_REQUEST_FAILED';
      errorStore.logError({
        success: false,
        code,
        message: fallbackMessage,
      });
      errorModalService.showErrorModal();
    };

    const showDownloadStartedDialog = (): void => {
      if (downloadStartedDialogRef) {
        return;
      }

      downloadStartedDialogRef = dialog.open(DialogComponent, {
        width: '500px',
        data: {
          title: 'Generic_DownloadStarted',
          message: 'Generic_DownloadStartedMessage',
          icon: 'info',
          actions: [
            {
              id: 'ok',
              label: 'Generic_OK',
              onClick: () => downloadStartedDialogRef?.close(false),
            },
          ],
        },
      });
      
      downloadStartedDialogRef.afterClosed().subscribe(() => {
        downloadStartedDialogRef = undefined;
      });
    };

    const mergeJobs = (updatedJobs: Job[]): void => {
      // Replace existing jobs by ID and add jobs that were not in the initial list.
      updateState(store, '[Download Jobs] Polling jobs merged', (state) => {
        const updatesById = new Map<string, Job>();
        updatedJobs.forEach((job) => {
          if (job.id) {
            updatesById.set(job.id, job);
          }
        });

        const mergedJobs = state.jobs.map((job) =>
          job.id ? updatesById.get(job.id) ?? job : job,
        );
        const existingIds = new Set(
          state.jobs
            .filter((job): job is Job & { id: string } => job.id !== undefined)
            .map((job) => job.id),
        );

        return {
          ...state,
          jobs: [
            ...mergedJobs,
            ...updatedJobs.filter((job) => job.id !== undefined && !existingIds.has(job.id)),
          ],
        };
      });
    };


    const reloadJobs = (): void => {
      // Queueing answers with a translation key rather than the job, so the list is re-read to pick
      // the new job up, and polling restarts if it is still outstanding.
      requestSubscription?.unsubscribe();
      requestSubscription = downloadJobsService.getJobs().subscribe({
        next: (response) => {
          if (!response.success || response.data === undefined) {
            reportResponseError(response.message, 'The download jobs could not be loaded.');
            return;
          }

          updateState(store, '[Download Jobs] Jobs reloaded', {
            jobs: response.data,
            jobsResponseStatus: ResponseStatus.LOADED,
          });
          startPollingIfNeeded();
        },
        error: (error: unknown) => {
          reportRequestError(error, 'The download jobs could not be loaded.');
        },
      });
    };

    /**
     * Re-reads the whole list. The polling request only carries jobs that are still running, so a
     * job that has just settled can only be learned about from a full read.
     */
    const refreshJobs = (stopAfterRefresh: boolean): void => {
      requestSubscription?.unsubscribe();
      requestSubscription = downloadJobsService.getJobs().subscribe({
        next: (response) => {
          if (!response.success || response.data === undefined) {
            reportResponseError(response.message, 'The download jobs could not be loaded.');
            if (stopAfterRefresh) {
              stopPolling();
            }
            return;
          }

          updateState(store, '[Download Jobs] Jobs refreshed', {
            jobs: response.data,
            jobsResponseStatus: ResponseStatus.LOADED,
          });

          if (stopAfterRefresh) {
            stopPolling();
          }
        },
        error: (error: unknown) => {
          if (stopAfterRefresh) {
            stopPolling();
          }
          reportRequestError(error, 'The download jobs could not be loaded.');
        },
      });
    };

    const startPollingIfNeeded = (): void => {
      if (store.pendingJobs().length === 0) {
        stopPolling();
        return;
      }

      if (pollingSubscription) {
        return;
      }

      updateState(store, '[Download Jobs] Polling started', {
        isPolling: true,
      });

      // Start checking for active jobs every 10 seconds.
      pollingSubscription = timer(POLLING_INTERVAL_MS, POLLING_INTERVAL_MS)
        // Ignore a new tick while the previous HTTP request is still running.
        .pipe(exhaustMap(() => downloadJobsService.getJobs(true)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.data === undefined) {
              reportResponseError(response.message, 'The download jobs could not be polled.');
              stopPolling();
              return;
            }

            // The poll asks for running jobs only, so a job drops out of the response the moment it
            // finishes. Merging can therefore never move a job off its last progress label - spotting
            // that it stopped being returned is the only signal that it settled, and a full read is
            // the only thing that carries its final status. Checked before the merge, while the
            // store still holds the previous poll's active set.
            const returnedIds = new Set(
              response.data
                .filter((job): job is Job & { id: string } => job.id !== undefined)
                .map((job) => job.id),
            );
            const hasSettledJob = store.pendingJobs()
              .some((job) => job.id !== undefined && !returnedIds.has(job.id));

            if (response.data.length > 0) {
              mergeJobs(response.data);
            }

            const nothingLeftRunning = response.data.filter(isActiveJob).length === 0;

            if (hasSettledJob || nothingLeftRunning) {
              // Keep polling while other jobs are still running; only the last one stops it.
              refreshJobs(nothingLeftRunning);
            }
          },
          error: (error: unknown) => {
            stopPolling();
            reportRequestError(error, 'The download jobs could not be polled.');
          },
        });
    };

    return {
      initialize(): void {
        if (store.initialized()) {
          return;
        }

        stopPolling();
        requestSubscription?.unsubscribe();
        updateState(store, '[Download Jobs] Loading jobs', {
          jobs: [],
          initialized: true,
          jobsResponseStatus: ResponseStatus.LOADING,
        });

        requestSubscription = downloadJobsService.getJobs().subscribe({
          next: (response) => {
            if (!response.success || response.data === undefined) {
              updateState(store, '[Download Jobs] Loading jobs failed', {
                initialized: false,
                jobsResponseStatus: ResponseStatus.ERROR,
              });
              reportResponseError(response.message, 'The download jobs could not be loaded.');
              return;
            }

            updateState(store, '[Download Jobs] Jobs loaded', {
              jobs: response.data,
              jobsResponseStatus: ResponseStatus.LOADED,
            });
            startPollingIfNeeded();
          },
          error: (error: unknown) => {
            updateState(store, '[Download Jobs] Loading jobs failed', {
              initialized: false,
              jobsResponseStatus: ResponseStatus.ERROR,
            });
            reportRequestError(error, 'The download jobs could not be loaded.');
          },
        });
      },

      reset(): void {
        stopPolling();
        requestSubscription?.unsubscribe();
        startDownloadSubscriptions.unsubscribe();
        requestSubscription = undefined;
        startDownloadSubscriptions = new Subscription();
        updateState(store, '[Download Jobs] Reset', initialState);
      },

      startDownload(request: StartDownloadRequest): void {
        updateState(store, '[Download Jobs] Starting download', {
          startJobResponseStatus: ResponseStatus.LOADING,
        });

        startDownloadSubscriptions.add(
          downloadJobsService.startDownload(request).subscribe({
            next: (response) => {
              if (!response.success || response.data === undefined) {
                updateState(store, '[Download Jobs] Starting download failed', {
                  startJobResponseStatus: ResponseStatus.ERROR,
                });
                reportResponseError(response.message, 'The download could not be started.');
                return;
              }

              updateState(store, '[Download Jobs] Download started', {
                startJobResponseStatus: ResponseStatus.LOADED,
              });
              showDownloadStartedDialog();
              reloadJobs();
            },
            error: (error: unknown) => {
              updateState(store, '[Download Jobs] Starting download failed', {
                startJobResponseStatus: ResponseStatus.ERROR,
              });
              reportRequestError(error, 'The download could not be started.');
            },
          }),
        );
      },
    };
  }),
);
